import { useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { handleAuthCallback } from '@/lib/auth-callback';

/**
 * Auth callback page - handles OAuth authorization code exchange.
 *
 * This page receives the authorization code from the auth server,
 * exchanges it for tokens, stores them, and redirects to the app.
 */
export function AuthCallback() {
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');

  useEffect(() => {
    // Check for error in URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const errorDesc = urlParams.get('error_description');

    if (errorParam) {
      setError(errorDesc || errorParam);
      setStatus('error');
      return;
    }

    // Check if we have an authorization code to exchange
    const code = urlParams.get('code');
    if (!code) {
      setError('No authorization code received');
      setStatus('error');
      return;
    }

    // Handle the auth callback - exchange code for tokens
    handleAuthCallback()
      .then((result) => {
        if (result.success) {
          console.log('[AuthCallback] Token exchange successful, page will reload');
          setStatus('success');
          // handleAuthCallback reloads the page on success
        } else if (result.error) {
          console.error('[AuthCallback] Token exchange failed:', result.error);
          setError(result.error);
          setStatus('error');
        } else {
          // No code found (shouldn't happen since we checked above)
          setError('Failed to process authentication');
          setStatus('error');
        }
      })
      .catch((err) => {
        console.error('[AuthCallback] Unexpected error:', err);
        setError(err.message || 'An unexpected error occurred');
        setStatus('error');
      });
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-grey-50">
        <div className="max-w-md p-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
            <AlertCircle className="h-6 w-6 text-error" aria-hidden />
          </div>
          <h1 className="mb-2 text-heading-3 text-secondary">Authentication Error</h1>
          <p className="mb-6 text-body-sm text-grey-500">{error}</p>
          <a href="/" className="btn-primary inline-flex">
            Try Again
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-grey-50">
      <div className="text-center">
        <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-accent" aria-hidden />
        <p className="text-body-sm text-grey-500" role="status">
          Completing authentication…
        </p>
      </div>
    </div>
  );
}
